import { useContext, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth0 } from "@auth0/auth0-react";
import {
  ArrowRight,
  BookOpen,
  BarChart3,
  Terminal,
  KeyRound,
  Play,
  Plus,
} from "lucide-react";
import { toast } from "react-toastify";
import { API_BASE, TOASTPROPS } from "../shared/Constants";
import { reportApiError, reportHandledError } from "../utils/reportError";
import { safeGetAccessToken } from "../utils/auth";
import { TitleContext } from "../contexts/TitleContext";
import { Button } from "./ui/button";
import { Skeleton } from "./ui/skeleton";
import Snippet from "./Code/Snippet";

interface PlanStatus {
  credits?: number;
  [key: string]: unknown;
}

const formatBRL = (value: number) =>
  value.toLocaleString("pt-BR", { style: "currency", currency: "BRL" });

export default function Info() {
  const navigate = useNavigate();
  const { setTitle } = useContext(TitleContext);
  const { getAccessTokenSilently, loginWithRedirect } = useAuth0();
  const [plan, setPlan] = useState<PlanStatus>();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const token = await safeGetAccessToken({ getAccessTokenSilently, loginWithRedirect });
        if (!token) return;
        const response = await fetch(`${API_BASE}/api/billing/status`, {
          headers: { Authorization: `Auth ${token}` },
        });
        if (response.ok) setPlan(await response.json());
        else {
          reportApiError(response, { feature: "info.fetch", method: "GET" });
          setPlan({ credits: 0 });
        }
      } catch (error) {
        reportHandledError(error, { category: "api", feature: "info.fetch" });
        toast.error("Erro ao carregar saldo.", TOASTPROPS);
      } finally {
        setLoading(false);
      }
    };
    fetchPlan();
  }, [getAccessTokenSilently, loginWithRedirect]);

  const go = (title: string, path: string) => {
    setTitle(title);
    navigate(path);
  };

  return (
    <section data-testid="platform-overview" className="home-overview">
      <header className="home-heading">
        <h1>Visão geral</h1>
        <p>Gerencie sua conta e acesse as ferramentas da API.</p>
      </header>

      <section data-testid="account-balance" className="home-balance" aria-label="Saldo da conta">
        <div>
          <h2>Saldo disponível</h2>
          {loading ? <Skeleton className="mt-[6px] h-[46.8px] w-40" /> : <p className="home-balance-value">{formatBRL(plan?.credits ?? 0)}</p>}
        </div>
        <div className="home-balance-actions">
          <Button onClick={() => go("Recarga", "/recarga")} className="gap-2"><Plus className="h-4 w-4" />Adicionar créditos</Button>
          <button className="home-billing-link" onClick={() => go("Recarga", "/recarga")}>Gerenciar cobrança <ArrowRight className="h-4 w-4" /></button>
        </div>
      </section>

      <section data-testid="next-actions" className="home-actions">
        <h2>Acesso rápido</h2>
        <div className="home-action-grid">
          <button onClick={() => go("Chaves de API", "/chaves-de-api")}>
            <KeyRound aria-hidden="true" />
            <strong>Criar chave de API</strong>
            <span>Crie e gerencie as credenciais da sua aplicação.</span>
            <ArrowRight aria-hidden="true" className="home-action-arrow" />
          </button>
          <button onClick={() => go("Playground", "/playground")}>
            <Play aria-hidden="true" />
            <strong>Abrir Playground</strong>
            <span>Teste os modelos e seus prompts.</span>
            <ArrowRight aria-hidden="true" className="home-action-arrow" />
          </button>
          <button onClick={() => go("Gastos", "/gastos")}>
            <BarChart3 aria-hidden="true" />
            <strong>Ver consumo</strong>
            <span>Acompanhe requisições, tokens e custos.</span>
            <ArrowRight aria-hidden="true" className="home-action-arrow" />
          </button>
        </div>
      </section>

      <section data-testid="quickstart-workspace" className="home-integration">
        <div className="home-integration-heading">
          <div><h2>Integre a API</h2><p>Use sua chave para fazer a primeira chamada.</p></div>
          <a href="https://docs.maritaca.ai/pt/api/comeco-rapido" target="_blank" rel="noreferrer"><BookOpen className="h-4 w-4" />Documentação da API</a>
        </div>
        <details className="essential-code-disclosure">
          <summary><Terminal className="h-4 w-4" /><span>Ver exemplo de integração</span><span className="essential-code-format">cURL</span></summary>
          <div className="essential-code-body"><Snippet /></div>
        </details>
      </section>
    </section>
  );
}
